import { BoxProps, Typography } from '@mui/material';
import { BoxHoverCursor } from './boxes';
import { ChangeEvent, useCallback, useRef } from 'react';
import { TEXTS } from '../../core/texts';
import { bytesToMB } from '../../utils/convertSize';
import { Loader } from './Loader';
import { useTheme } from '@mui/material/styles';
import { useNotification } from '../../+state/useNotification';

export interface IUploadProps extends BoxProps {
  accept: string[];
  multiple?: boolean;
  handleFileUpload: (file: File) => void;
  maxByteSize: number,
  allowedExtensions?: string[];
  isLoading?: boolean,
  error?: string | null,
}

const WithUpload = ({
                      accept,
                      multiple,
                      handleFileUpload,
                      maxByteSize,
                      children,
                      allowedExtensions,
                      isLoading,
                      error,
                      ...props
                    }: IUploadProps) => {
  const theme = useTheme();
  const { openErrorToast } = useNotification();
  const ref = useRef<HTMLInputElement>(null);
  const onFileUpload = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > maxByteSize) {
        return openErrorToast(TEXTS.NOT_ALLOWED_SIZE(bytesToMB(maxByteSize)));
      }
      const { length, [length - 1]: extName } = file.name.split('.');
      if (allowedExtensions && !allowedExtensions.find(ext => ext.toLowerCase() === extName.toLowerCase())) {
        return openErrorToast(TEXTS.NOT_ALLOWED_EXTENSION);
      }
      handleFileUpload(file);
    }
    if (ref.current) {
      ref.current.value = '';
    }
  }, [allowedExtensions, handleFileUpload, maxByteSize, openErrorToast]);

  const onClickHandler = useCallback(() => ref?.current?.click(), []);

  return (
    <BoxHoverCursor {...props} onClick={onClickHandler} position={'relative'}>
      {children}
      {
        isLoading &&
        <Loader loaderColor={'primary'} width={30} mt={.5}/>
      }
      <input ref={ref} hidden accept={accept.join(',')} multiple={multiple}
             type={'file'} onChange={onFileUpload}/>
      {
        error &&
        <Typography sx={{ color: theme.palette.error.main, mt: 1 }} variant={'body2'}>
          {error}
        </Typography>
      }
    </BoxHoverCursor>
  );
};

export default WithUpload;